import { osnovaApi } from '../base';
import { parseResponse } from '../parseResponse';
import { uploadWithProgress, type UploadProgress } from '../uploadWithProgress';
import { parseFilenameFromDisposition } from '@shared/lib/downloadFile';
import type { AppDispatch } from '@app/store';
import type {
  PrcBlockName,
  PrcBreakEven,
  PrcDiff,
  PrcLimitsBlock,
  PrcLimitsDetail,
  PrcLimitsListItem,
  PrcModelPrice,
  PrcParams,
  PrcPayroll,
  PrcPrices,
  PrcVersionModel,
  PrcVersionPayload,
  PrcVersionSummary,
} from '../types';

export type { UploadProgress };

/** Объект из шапки ОСНОВЫ: только его версии и общие, его действующие ПРЦ. */
export interface PrcObjectScope {
  project_id?: string;
}

/**
 * ПРЦ машино-часа Экономиста (`/api/v1/agents/econ/prc`, ТЗ AI-Economist-2026
 * ред. 2.1, п. 6.2, 7.2): версии расчёта, блоки «Цены», «ФОТ», параметры,
 * лимиты на ремонт, «было → стало», безубыточность, утверждение в справочник
 * техники.
 *
 * Любая правка блока пересчитывает все модели и отвечает тем же payload, что
 * `GET /versions/{id}` за базовый год. Ответ сразу кладётся в кеш версии
 * (`head:<id>`), поэтому шапка и вкладки не ждут повторного GET; всё остальное,
 * что зависит от версии (другие годы, модель, «было → стало», лимиты,
 * безубыточность), помечено тегом id версии и перечитывается.
 *
 * Варианты условий (миграция 0336): чтение и правки принимают `variant` —
 * ответ тогда в условиях варианта и кладётся в его запись кеша. Записи
 * вариантов помечены тегом `variants:<id>`: правка основного расчёта
 * пересчитывает и варианты, поэтому они перечитываются; правка варианта
 * меняет `updated_at` версии — перечитывается и основной расчёт.
 */

const BASE = '/agents/econ/prc';

type PrcTag = { type: 'EconPrc'; id: string };
const LIST: PrcTag = { type: 'EconPrc', id: 'LIST' };
const CURRENT: PrcTag = { type: 'EconPrc', id: 'CURRENT' };
const headTag = (id: string): PrcTag => ({ type: 'EconPrc', id: `head:${id}` });
const versionTag = (id: string): PrcTag => ({ type: 'EconPrc', id });
const variantsTag = (id: string): PrcTag => ({ type: 'EconPrc', id: `variants:${id}` });

/** Что перечитать после правки: основного расчёта — варианты (они следуют за
 *  ним), варианта — и основной расчёт (сменился `updated_at` версии). */
function editedTags(id: string, variant: string | undefined): PrcTag[] {
  return variant
    ? [versionTag(id), headTag(id), variantsTag(id), LIST]
    : [versionTag(id), variantsTag(id), LIST];
}

export interface PrcVersionArg {
  id: string;
  /** Год строк моделей; нет — базовый год версии. */
  year?: number;
  /** Вариант условий; нет — основной расчёт. */
  variant?: string;
}

/**
 * Аргумент запроса версии. Базовый год передавайте как `undefined`: тогда экран
 * и ответы правок делят одну запись кеша. С вариантом так же: нет варианта —
 * основной расчёт.
 */
export function prcVersionArg(id: string, year?: number, variant?: string | null): PrcVersionArg {
  const arg: PrcVersionArg = { id };
  if (year !== undefined) arg.year = year;
  if (variant) arg.variant = variant;
  return arg;
}

/** Правка блока: `expected_updated_at` — `updated_at` версии, от которой начата
 *  правка; `variant_id` — правка в условиях варианта. */
export interface PrcBlockSave<T> {
  id: string;
  expected_updated_at: string;
  block: T;
  variant_id?: string;
}

/** Файл ответа: `File` с именем из `Content-Disposition`; ошибку — как JSON. */
async function fileResponse(response: Response): Promise<unknown> {
  if (!response.ok) return parseResponse(response);
  const blob = await response.blob();
  const name = parseFilenameFromDisposition(response.headers.get('content-disposition'));
  return name ? new File([blob], name, { type: blob.type }) : blob;
}

export const prcApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Версии расчёта, новые сверху; с объектом — его версии и общие. */
    listPrcVersions: b.query<{ items: PrcVersionSummary[] }, PrcObjectScope | void>({
      query: (scope) => ({
        url: `${BASE}/versions`,
        params: scope && scope.project_id ? { project_id: scope.project_id } : undefined,
      }),
      providesTags: [LIST],
    }),

    /** Версия: шапка, блоки, итоги и строки моделей за год; с вариантом — в его
     *  условиях. */
    getPrcVersion: b.query<PrcVersionPayload, PrcVersionArg>({
      query: ({ id, year, variant }) => ({
        url: `${BASE}/versions/${id}`,
        params: { year, variant_id: variant },
      }),
      providesTags: (_r, _e, { id, year, variant }) =>
        year !== undefined ? [versionTag(id)] : variant ? [variantsTag(id)] : [headTag(id)],
    }),

    /** Черновик-копия: без `base_version_id` — копия последней утверждённой
     *  (объекта, если он задан). `project_ids` — объекты версии, пусто — общая. */
    createPrcVersion: b.mutation<
      PrcVersionPayload,
      { label: string; base_version_id?: string; comment?: string; project_ids?: string[] }
    >({
      query: (body) => ({ url: `${BASE}/versions`, method: 'POST', body }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      invalidatesTags: (r) => (r ? [LIST] : []),
    }),

    /**
     * Черновик из книги ЭММ: multipart с файлом и полем `label`. Передача файла
     * идёт с прогрессом (`onProgress`), потом сервер разбирает книгу и сверяет
     * расчёт с Excel — это до минуты.
     */
    importPrcBook: b.mutation<
      PrcVersionPayload,
      {
        file: File;
        label?: string;
        /** Объекты черновика; пусто — общая версия. */
        project_ids?: readonly string[];
        onProgress?: (p: UploadProgress) => void;
      }
    >({
      async queryFn({ file, label, project_ids, onProgress }, api) {
        const res = await uploadWithProgress(
          api,
          `${BASE}/import`,
          () => {
            const form = new FormData();
            form.append('file', file, file.name);
            const name = label?.trim();
            if (name) form.append('label', name);
            if (project_ids && project_ids.length > 0) {
              form.append('project_ids', project_ids.join(','));
            }
            return form;
          },
          onProgress,
        );
        if ('error' in res) return res;
        // Ответ — тот же payload версии, что у GET /versions/{id}; форму ответа
        // RTK Query не проверяет и у остальных эндпоинтов.
        return { data: res.data as PrcVersionPayload };
      },
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      invalidatesTags: (r) => (r ? [LIST] : []),
    }),

    /** Объекты черновика: к каким объектам относится расчёт (пусто — общий). */
    setPrcVersionObjects: b.mutation<
      PrcVersionPayload,
      { id: string; project_ids: string[]; expected_updated_at: string }
    >({
      query: ({ id, project_ids, expected_updated_at }) => ({
        url: `${BASE}/versions/${id}/objects`,
        method: 'PUT',
        body: { project_ids, expected_updated_at },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      invalidatesTags: (r, _e, { id }) => (r ? editedTags(id, undefined) : []),
    }),

    /**
     * Вариант условий черновика: без `from_version_id` — как основной расчёт,
     * с ним — свои разделы те, чем блоки той версии отличаются от основного
     * расчёта (отдельно загруженная книга района становится вариантом).
     */
    createPrcVariant: b.mutation<
      PrcVersionPayload,
      {
        id: string;
        name: string;
        project_ids: string[];
        from_version_id?: string;
        expected_updated_at: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `${BASE}/versions/${id}/variants`,
        method: 'POST',
        body,
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      invalidatesTags: (r, _e, { id }) =>
        r ? [versionTag(id), headTag(id), variantsTag(id), LIST] : [],
    }),

    /** Переименовать вариант или сменить его объекты. */
    updatePrcVariant: b.mutation<
      PrcVersionPayload,
      {
        id: string;
        variantId: string;
        name?: string;
        project_ids?: string[];
        expected_updated_at: string;
      }
    >({
      query: ({ id, variantId, ...body }) => ({
        url: `${BASE}/versions/${id}/variants/${variantId}`,
        method: 'PUT',
        body,
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      invalidatesTags: (r, _e, { id, variantId }) => (r ? editedTags(id, variantId) : []),
    }),

    /** Удалить вариант черновика; ответ — основной расчёт. */
    deletePrcVariant: b.mutation<
      PrcVersionPayload,
      { id: string; variantId: string; expected_updated_at: string }
    >({
      query: ({ id, variantId, expected_updated_at }) => ({
        url: `${BASE}/versions/${id}/variants/${variantId}`,
        method: 'DELETE',
        body: { expected_updated_at },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      invalidatesTags: (r, _e, { id }) => (r ? editedTags(id, undefined) : []),
    }),

    /** Вернуть раздел варианта «как в основном расчёте». */
    resetPrcVariantSection: b.mutation<
      PrcVersionPayload,
      {
        id: string;
        variantId: string;
        block: PrcBlockName;
        section: string;
        expected_updated_at: string;
      }
    >({
      query: ({ id, variantId, block, section, expected_updated_at }) => ({
        url: `${BASE}/versions/${id}/variants/${variantId}/reset`,
        method: 'POST',
        body: { block, section, expected_updated_at },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      invalidatesTags: (r, _e, { id, variantId }) => (r ? editedTags(id, variantId) : []),
    }),

    savePrcPrices: b.mutation<PrcVersionPayload, PrcBlockSave<PrcPrices>>({
      query: ({ id, block, expected_updated_at, variant_id }) => ({
        url: `${BASE}/versions/${id}/prices`,
        method: 'PUT',
        body: { prices: block, expected_updated_at, variant_id },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      invalidatesTags: (r, _e, { id, variant_id }) => (r ? editedTags(id, variant_id) : []),
    }),

    savePrcPayroll: b.mutation<PrcVersionPayload, PrcBlockSave<PrcPayroll>>({
      query: ({ id, block, expected_updated_at, variant_id }) => ({
        url: `${BASE}/versions/${id}/payroll`,
        method: 'PUT',
        body: { payroll: block, expected_updated_at, variant_id },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      invalidatesTags: (r, _e, { id, variant_id }) => (r ? editedTags(id, variant_id) : []),
    }),

    savePrcParams: b.mutation<PrcVersionPayload, PrcBlockSave<PrcParams>>({
      query: ({ id, block, expected_updated_at, variant_id }) => ({
        url: `${BASE}/versions/${id}/params`,
        method: 'PUT',
        body: { params: block, expected_updated_at, variant_id },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      invalidatesTags: (r, _e, { id, variant_id }) => (r ? editedTags(id, variant_id) : []),
    }),

    /** «+N % на категорию»: база и ручные цены лет категории × (1 + N / 100). */
    bulkPrcPrices: b.mutation<
      PrcVersionPayload,
      {
        id: string;
        category: string;
        value: string;
        expected_updated_at: string;
        variant_id?: string;
      }
    >({
      query: ({ id, category, value, expected_updated_at, variant_id }) => ({
        url: `${BASE}/versions/${id}/bulk`,
        method: 'POST',
        body: { category, op: 'percent', value, expected_updated_at, variant_id },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      invalidatesTags: (r, _e, { id, variant_id }) => (r ? editedTags(id, variant_id) : []),
    }),

    /** Подтянуть в черновик свежие паспорта из справочника техники. */
    refreshPrcModels: b.mutation<
      PrcVersionPayload,
      { id: string; expected_updated_at: string; variant_id?: string }
    >({
      query: ({ id, expected_updated_at, variant_id }) => ({
        url: `${BASE}/versions/${id}/refresh-models`,
        method: 'POST',
        body: { expected_updated_at, variant_id },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      // Пересчитываются все условия; ответ — в условиях экрана.
      invalidatesTags: (r, _e, { id, variant_id }) => (r ? editedTags(id, variant_id) : []),
    }),

    /**
     * Отметки «утверждать»: пустой `model_ids` — все модели. Отметка видна сразу,
     * при отказе сервера возвращается. `year` — год, который сейчас на экране.
     */
    setPrcSelection: b.mutation<
      PrcVersionPayload,
      { id: string; model_ids: string[]; selected: boolean; year?: number; variant?: string }
    >({
      query: ({ id, model_ids, selected, variant }) => ({
        url: `${BASE}/versions/${id}/selection`,
        method: 'PUT',
        body: { model_ids, selected, variant_id: variant },
      }),
      async onQueryStarted(
        { id, model_ids, selected, year, variant },
        { dispatch, queryFulfilled },
      ) {
        const all = model_ids.length === 0;
        const ids = new Set(model_ids);
        const mark = (draft: PrcVersionPayload) => {
          for (const m of draft.models) if (all || ids.has(m.model_id)) m.selected = selected;
          draft.summary.selected = draft.models.filter((m) => m.selected).length;
        };
        const patches = [
          dispatch(
            prcApi.util.updateQueryData(
              'getPrcVersion',
              prcVersionArg(id, undefined, variant),
              mark,
            ),
          ),
        ];
        if (year !== undefined) {
          patches.push(
            dispatch(
              prcApi.util.updateQueryData('getPrcVersion', prcVersionArg(id, year, variant), mark),
            ),
          );
        }
        try {
          const { data } = await queryFulfilled;
          void dispatch(
            prcApi.util.upsertQueryData(
              'getPrcVersion',
              prcVersionArg(id, undefined, data.variant?.id),
              data,
            ),
          );
        } catch {
          for (const p of patches) p.undo();
        }
      },
      // Отметки общие для всех условий: перечитать и основной расчёт, и варианты.
      invalidatesTags: (r, _e, { id, variant }) =>
        r
          ? variant
            ? [versionTag(id), headTag(id), variantsTag(id), LIST]
            : [versionTag(id), variantsTag(id), LIST]
          : [],
    }),

    submitPrcVersion: b.mutation<PrcVersionPayload, { id: string; comment?: string }>({
      query: ({ id, comment }) => ({
        url: `${BASE}/versions/${id}/submit`,
        method: 'POST',
        body: comment ? { comment } : {},
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      invalidatesTags: (r, _e, { id }) => (r ? editedTags(id, undefined) : []),
    }),

    /**
     * Утвердить: ПРЦ отмеченных моделей уходит в справочник техники с датой
     * «действует с» — меняются и действующие цены, и пометки «ПРЦ устарела».
     */
    approvePrcVersion: b.mutation<
      PrcVersionPayload,
      { id: string; valid_from: string; comment?: string }
    >({
      query: ({ id, valid_from, comment }) => ({
        url: `${BASE}/versions/${id}/approve`,
        method: 'POST',
        body: comment ? { valid_from, comment } : { valid_from },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      invalidatesTags: (r, _e, { id }) =>
        r ? [...editedTags(id, undefined), CURRENT, 'Fleet'] : [],
    }),

    /** Вернуть на доработку — причина обязательна. */
    rejectPrcVersion: b.mutation<PrcVersionPayload, { id: string; comment: string }>({
      query: ({ id, comment }) => ({
        url: `${BASE}/versions/${id}/reject`,
        method: 'POST',
        body: { comment },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      invalidatesTags: (r, _e, { id }) => (r ? editedTags(id, undefined) : []),
    }),

    /** Удалить черновик или возвращённую версию — автору или админу. */
    deletePrcVersion: b.mutation<{ deleted: string }, string>({
      query: (id) => ({ url: `${BASE}/versions/${id}`, method: 'DELETE' }),
      invalidatesTags: (r) => (r ? [LIST] : []),
    }),

    /** Модель версии целиком: снимок паспорта, все годы, промежуточные величины. */
    getPrcModelResult: b.query<
      { model: PrcVersionModel },
      { id: string; modelId: string; variant?: string }
    >({
      query: ({ id, modelId, variant }) => ({
        url: `${BASE}/versions/${id}/results`,
        params: { model_id: modelId, variant_id: variant },
      }),
      providesTags: (_r, _e, { id }) => [versionTag(id)],
    }),

    /** «Было → стало»: `against` — `current` (действующие ПРЦ), `base` (основной
     *  расчёт — для варианта) или id версии. */
    getPrcDiff: b.query<PrcDiff, { id: string; against?: string; year?: number; variant?: string }>(
      {
        query: ({ id, against, year, variant }) => ({
          url: `${BASE}/versions/${id}/diff`,
          params: { against, year, variant_id: variant },
        }),
        providesTags: (_r, _e, { id, against }) =>
          against && against !== 'current' && against !== 'base'
            ? [versionTag(id), versionTag(against), headTag(against)]
            : [versionTag(id), CURRENT],
      },
    ),

    /** Безубыточность и чувствительность по машине — блоки 1–4 формы. */
    getPrcBreakEven: b.query<
      PrcBreakEven,
      {
        id: string;
        modelId: string;
        /** Вид расчёта: 1 — коммерческий, 2 — управленческий. */
        variant: 1 | 2;
        year?: number;
        sensitivity?: string;
        /** Вариант условий; нет — основной расчёт. */
        conditions?: string;
      }
    >({
      query: ({ id, modelId, variant, year, sensitivity, conditions }) => ({
        url: `${BASE}/versions/${id}/break-even/${modelId}`,
        params: { variant, year, sensitivity, variant_id: conditions },
      }),
      providesTags: (_r, _e, { id }) => [versionTag(id)],
    }),

    /** Модели версии: у кого есть перечень лимитов и норма по нему. */
    listPrcLimits: b.query<{ items: PrcLimitsListItem[]; updated_at: string }, string>({
      query: (id) => `${BASE}/versions/${id}/repair-limits`,
      providesTags: (_r, _e, id) => [versionTag(id)],
    }),

    /** Перечень модели и его расчёт формулами ОСНОВЫ. */
    getPrcLimits: b.query<PrcLimitsDetail, { id: string; modelId: string }>({
      query: ({ id, modelId }) => ({
        url: `${BASE}/versions/${id}/repair-limits`,
        params: { model_id: modelId },
      }),
      providesTags: (_r, _e, { id }) => [versionTag(id)],
    }),

    /** Сохранить перечень модели (`block: null` — убрать, норма из паспорта). */
    savePrcLimits: b.mutation<
      PrcVersionPayload,
      { id: string; modelId: string; block: PrcLimitsBlock | null; expected_updated_at: string }
    >({
      query: ({ id, modelId, block, expected_updated_at }) => ({
        url: `${BASE}/versions/${id}/repair-limits/${modelId}`,
        method: 'PUT',
        body: { block, expected_updated_at },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) =>
        storeVersion(dispatch, queryFulfilled),
      invalidatesTags: (r, _e, { id }) => (r ? editedTags(id, undefined) : []),
    }),

    /** Действующие ПРЦ справочника техники. */
    /** Действующие ПРЦ: с объектом — по одной на модель (своя или общая), без
     *  объекта — все, общие и по каждому объекту. */
    listPrcCurrent: b.query<{ items: PrcModelPrice[] }, PrcObjectScope | void>({
      query: (scope) => ({
        url: `${BASE}/current`,
        params: scope && scope.project_id ? { project_id: scope.project_id } : undefined,
      }),
      providesTags: [CURRENT],
    }),

    /** Утверждённые ПРЦ модели, свежие сверху. */
    getPrcModelHistory: b.query<{ items: PrcModelPrice[] }, string>({
      query: (modelId) => `${BASE}/models/${modelId}/history`,
      providesTags: [CURRENT],
    }),

    /**
     * Выгрузка xlsx: без модели — лист «Расчет машино-час», с моделью — ещё
     * «Калькуляция (форма)» по машине. Как у справочника техники, Blob в Redux не
     * попадает: наружу — object URL и имя файла из `Content-Disposition`.
     */
    exportPrcXlsx: b.mutation<
      { url: string; name: string | null },
      { id: string; modelId?: string; variant?: string }
    >({
      async queryFn({ id, modelId, variant }, _api, _extra, baseQuery) {
        const params: Record<string, string> = {};
        if (modelId) params.model_id = modelId;
        if (variant) params.variant_id = variant;
        const res = await baseQuery({
          url: `${BASE}/versions/${id}/export.xlsx`,
          params: Object.keys(params).length > 0 ? params : undefined,
          cache: 'no-store',
          responseHandler: fileResponse,
        });
        if (res.error) return { error: res.error };
        const file = res.data;
        if (!(file instanceof Blob)) {
          return { error: { status: 'CUSTOM_ERROR', error: 'Сервер вернул не файл.' } };
        }
        return {
          data: {
            url: URL.createObjectURL(file),
            name: file instanceof File && file.name ? file.name : null,
          },
        };
      },
    }),
  }),
  overrideExisting: false,
});

/** Ответ правки версии — сразу в кеш версии за базовый год: основного расчёта
 *  или варианта, в чьих условиях ответ. */
async function storeVersion(
  dispatch: AppDispatch,
  queryFulfilled: Promise<{ data: PrcVersionPayload }>,
): Promise<void> {
  try {
    const { data } = await queryFulfilled;
    void dispatch(
      prcApi.util.upsertQueryData(
        'getPrcVersion',
        prcVersionArg(data.version.id, undefined, data.variant?.id),
        data,
      ),
    );
  } catch {
    /* ошибку показывает экран через unwrap() */
  }
}

export const {
  useListPrcVersionsQuery,
  useGetPrcVersionQuery,
  useLazyGetPrcVersionQuery,
  useCreatePrcVersionMutation,
  useImportPrcBookMutation,
  useSetPrcVersionObjectsMutation,
  useCreatePrcVariantMutation,
  useUpdatePrcVariantMutation,
  useDeletePrcVariantMutation,
  useResetPrcVariantSectionMutation,
  useSavePrcPricesMutation,
  useSavePrcPayrollMutation,
  useSavePrcParamsMutation,
  useBulkPrcPricesMutation,
  useRefreshPrcModelsMutation,
  useSetPrcSelectionMutation,
  useSubmitPrcVersionMutation,
  useApprovePrcVersionMutation,
  useRejectPrcVersionMutation,
  useDeletePrcVersionMutation,
  useGetPrcModelResultQuery,
  useGetPrcDiffQuery,
  useGetPrcBreakEvenQuery,
  useListPrcLimitsQuery,
  useGetPrcLimitsQuery,
  useLazyGetPrcLimitsQuery,
  useSavePrcLimitsMutation,
  useListPrcCurrentQuery,
  useGetPrcModelHistoryQuery,
  useExportPrcXlsxMutation,
} = prcApi;
